import { supabase } from '../client'
import type { CloudUserPreference } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── USER PREFERENCES (Cloud) ─────────────────────────────────────────── */

// Synced key/value UI settings — one row per (user, key). See the
// user_preferences migration for why local_id carries the key.

export async function getCloudUserPreferences(userId: string, since?: Date): Promise<CloudUserPreference[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get user preferences error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get user preferences error', error)
    return []
  }
}

export async function getCloudUserPreferenceIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)
    if (error) {
      logSupabaseError('Get user preference IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get user preference IDs error', error)
    return []
  }
}

export async function getCloudUserPreferencesByIds(userId: string, ids: string[]): Promise<CloudUserPreference[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get user preferences by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get user preferences by IDs error', error)
    return []
  }
}

export async function getDeletedCloudUserPreferences(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('user_preferences')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted user preferences error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted user preferences error', error)
    return []
  }
}

export async function createCloudUserPreference(
  userId: string,
  preference: Omit<CloudUserPreference, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudUserPreference | null> {
  if (!supabase) return null
  try {
    // UPSERT on (user_id, key): one row per key, and a second device may have
    // inserted it first. A plain insert would hit the unique constraint and
    // drop the write.
    const { data, error } = await supabase
      .from('user_preferences')
      .upsert({ ...preference, user_id: userId }, { onConflict: 'user_id,key' })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create user preference error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create user preference error', error)
    return null
  }
}

export async function updateCloudUserPreference(
  preferenceId: string,
  updates: Partial<Omit<CloudUserPreference, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('user_preferences')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', preferenceId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update user preference error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update user preference error', error)
    return null
  }
}

export async function deleteCloudUserPreference(preferenceId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('user_preferences')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', preferenceId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete user preference error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete user preference error', error)
    return false
  }
}
